import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useCompare } from "../context/CompareContext";
import { AvailabilityBadge } from "../components/StatusBadge";
import { X, ArrowLeft, Ban } from "lucide-react";

const ROWS = [
  ["Availability", (p) => `${p.available_beds} beds`],
  ["Last updated", (p) => (p.last_updated ? new Date(p.last_updated).toLocaleString() : "never")],
  ["ASAM level", (p) => `${p.asam_level_code} — ${p.asam_official_name || ""}`],
  ["Population", (p) => p.population],
  ["Sex designation", (p) => p.sex_designation],
  ["Pregnancy / parenting", (p) => (p.pregnancy_parenting ? "Yes" : "—")],
  ["MOUD/MAT", (p) => (p.moud ? "Yes" : "—")],
  ["Co-occurring", (p) => (p.cooccurring ? "Yes" : "—")],
  ["Payers accepting", (p) => (p.payers || []).filter((x) => x.contracted && x.accepting).map((x) => x.payer).join(", ") || "—"],
  ["Transportation", (p) => (p.transportation?.status || "unavailable").replace(/_/g, " ")],
  ["Admission hours", (p) => p.admission_hours || "—"],
  ["Intake phone", (p) => p.intake_phone || "—"],
];

export default function Compare() {
  const { ids, remove, clear } = useCompare();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ids.length === 0) {
      setPrograms([]);
      setLoading(false);
      return;
    }
    api.get("/compare", { params: { ids: ids.join(",") } }).then((r) => setPrograms(r.data)).finally(() => setLoading(false));
  }, [ids]);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <Link to="/search" className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:underline">
            <ArrowLeft size={15} /> Back to directory
          </Link>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Compare programs</h1>
          <p className="text-sm text-slate-500">Up to four programs. This view does not score, rank, or recommend providers.</p>
        </div>
        <div className="flex items-center gap-2">
          <button disabled data-testid="export-btn" title="Disabled in this release" className="cursor-not-allowed rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-400">
            <span className="flex items-center gap-1"><Ban size={14} /> Export</span>
          </button>
          <button disabled data-testid="print-btn" title="Disabled in this release" className="cursor-not-allowed rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-400">
            <span className="flex items-center gap-1"><Ban size={14} /> Print</span>
          </button>
          {ids.length > 0 && (
            <button onClick={clear} data-testid="clear-compare" className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800">
              Clear all
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-slate-500">Loading…</div>
      ) : programs.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
          No programs selected. Add up to four programs from the directory.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 w-48 bg-slate-50 p-4 text-left text-xs font-bold uppercase tracking-widest text-slate-400">Field</th>
                {programs.map((p) => (
                  <th key={p.id} className="min-w-[220px] border-l border-slate-100 p-4 text-left align-top">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        {p.photo_url ? (
                          <img src={p.photo_url} alt={p.facility_name} data-testid={`compare-photo-${p.id}`} className="mb-2 h-24 w-full rounded-lg border border-slate-200 object-cover" />
                        ) : (
                          <div data-testid={`compare-photo-placeholder-${p.id}`} className="mb-2 flex h-24 w-full items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-xs font-medium text-slate-400">No photo</div>
                        )}
                        <div className="font-display text-base font-bold text-slate-900">{p.facility_name}</div>
                        <div className="text-xs text-slate-500">{p.name}</div>
                        <div className="mt-2"><AvailabilityBadge beds={p.available_beds} freshness={p.freshness} /></div>
                      </div>
                      <button data-testid={`remove-compare-${p.id}`} onClick={() => remove(p.id)} className="rounded p-1 text-slate-400 hover:bg-slate-100">
                        <X size={16} />
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([label, fn], i) => (
                <tr key={label} className={i % 2 ? "bg-slate-50/50" : ""}>
                  <td className="sticky left-0 z-10 bg-inherit p-4 text-xs font-bold uppercase tracking-widest text-slate-400">{label}</td>
                  {programs.map((p) => (
                    <td key={p.id} className="border-l border-slate-100 p-4 font-medium capitalize text-slate-800">{fn(p) || "—"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
